import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Talk from 'talkjs';
import { UnreadConversation } from 'talkjs/types/talk.types';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/modules/core/services/translation.service';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { LocalStoreService } from './local-store.service';

// Customer ↔ support chat, riding the same TalkJS infrastructure as the
// race-room chat but through its own session + popup so the two features
// never interfere (race unread counters must not include support threads,
// and vice versa).
//
// Flow: one POST /chat/support/start (backend syncs users, ensures the
// conversation, mints the identity signature) → mount a TalkJS popup on the
// returned conversation. The popup survives SPA route changes; it is
// destroyed on logout.

interface SupportStartResponse {
  error: boolean;
  data: {
    conversationId: string;
    status: 'open' | 'resolved';
    signature: string;
    appId: string;
    supportAgentId: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class SupportChatService {
  private readonly isBrowser: boolean;
  private session: Talk.Session | null = null;
  private popup: Talk.Popup | null = null;
  private sessionUid: string | null = null;
  private opening = false;
  private originalViewport: string | null = null;

  /** Unread count for the support conversation only (0 until first open). */
  unreadCount$ = new BehaviorSubject<number>(0);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private translation: TranslationService,
    private storageService: LocalStoreService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      // Tear the popup down when the user logs out OR when the signed-in
      // account CHANGES (Firebase auth state is shared across tabs, so
      // logging in as another user elsewhere must not leave a popup bound
      // to the previous identity).
      this.authService.authStatusSub.subscribe((credential: any) => {
        const uid = credential?.uid || null;
        if (this.sessionUid && uid !== this.sessionUid) {
          this.destroy();
        }
      });
    }
  }

  /**
   * Opens the support chat popup, creating the TalkJS session on first use.
   * Returns 'login-required' for anonymous visitors (every support route
   * requires an authenticated account) so the caller can open the login
   * popup. Throws on backend/TalkJS failure so the caller can show an
   * "unavailable" message.
   */
  async open(): Promise<'opened' | 'login-required'> {
    if (!this.isBrowser || this.opening) {
      return 'opened';
    }

    const user = (this.storageService.getItem('user') as any)?.user;
    if (!user?.uid) {
      return 'login-required';
    }

    // Cached popup is only valid for the SAME account.
    if (this.popup && this.sessionUid === user.uid) {
      this.popup.show();
      this.lockViewportForMobile();
      return 'opened';
    }
    if (this.popup && this.sessionUid !== user.uid) {
      this.destroy();
    }

    this.opening = true;
    try {
      const start = await this.startConversation();
      await Talk.ready;

      const me = new Talk.User(user.uid);
      this.session = new Talk.Session({
        // Backend env is the source of truth; environment.ts is the fallback
        // so a stale local build still points somewhere sensible.
        appId: start.appId || environment.talkjsAppID,
        me,
        signature: start.signature,
      });
      this.sessionUid = user.uid;
      this.watchUnreads(this.session, start.conversationId);

      const conversation = this.session.getOrCreateConversation(
        start.conversationId
      );
      this.popup = this.session.createPopup({
        keepOpen: false,
        launcher: 'close-only',
        showCloseInHeader: 'auto',
      });
      // Restore normal pinch-zoom whenever the popup is closed via its
      // header X (mobile) or close button — onClose does NOT fire on our own
      // hide()/destroy(), which we handle explicitly.
      this.popup.onClose(() => this.restoreViewport());

      this.popup.select(conversation);
      await this.popup.mount({ show: true });
      this.lockViewportForMobile();
      return 'opened';
    } catch (e) {
      // 403 = kill-switch off; anything else = transient failure. Reset so
      // the next click can retry cleanly; the caller shows the message.
      this.destroy();
      throw e;
    } finally {
      this.opening = false;
    }
  }

  private startConversation(): Promise<SupportStartResponse['data']> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'accept-language': this.currentLanguage(),
      }),
    };
    const url = environment.apiUrl + '/chat/support/start';
    return this.http
      .post<SupportStartResponse>(url, {}, options)
      .toPromise()
      .then((res) => res.data);
  }

  private currentLanguage(): string {
    const currentLang = this.translation?.langSubject?.value;
    if (currentLang) {
      return currentLang;
    }
    const storedLang = window.localStorage.getItem('language');
    return storedLang || 'fr';
  }

  private watchUnreads(session: Talk.Session, conversationId: string): void {
    session.unreads.onChange((conversations: UnreadConversation[]) => {
      const count = conversations.filter(
        (c) => c.lastMessage?.conversation?.id === conversationId
      ).length;
      this.unreadCount$.next(count);
    });
  }

  private destroy(): void {
    if (this.popup) {
      this.popup.destroy();
      this.popup = null;
    }
    if (this.session) {
      this.session.destroy();
      this.session = null;
    }
    this.sessionUid = null;
    this.unreadCount$.next(0);
    this.restoreViewport();
  }

  // iOS Safari auto-zooms the page when focusing an input whose font-size is
  // < 16px. The chat input lives inside TalkJS's cross-origin iframe, so we
  // can't touch its font-size — instead we pin the page viewport for the
  // duration of the chat (which is full-screen on mobile anyway) and restore
  // normal pinch-zoom the moment it closes. Scoped to small screens so
  // desktop and site-wide accessibility zoom are untouched.
  private lockViewportForMobile(): void {
    if (!this.isBrowser || typeof document === 'undefined') {
      return;
    }
    if (!window.matchMedia || !window.matchMedia('(max-width: 1024px)').matches) {
      return;
    }
    const meta = document.querySelector(
      'meta[name="viewport"]'
    ) as HTMLMetaElement | null;
    if (!meta) {
      return;
    }
    if (this.originalViewport === null) {
      this.originalViewport = meta.getAttribute('content');
    }
    meta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0'
    );
  }

  private restoreViewport(): void {
    if (!this.isBrowser || typeof document === 'undefined') {
      return;
    }
    if (this.originalViewport === null) {
      return;
    }
    const meta = document.querySelector(
      'meta[name="viewport"]'
    ) as HTMLMetaElement | null;
    if (meta) {
      meta.setAttribute('content', this.originalViewport);
    }
    this.originalViewport = null;
  }
}
